import {
  Button,
  Heading,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useAuth } from "@clerk/clerk-react";
import { RiCheckFill } from "@remixicon/react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router";

import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { useSlugParam } from "@/common/hooks/useParam";
import api from "@/common/utils/api";
import { translateError } from "@/common/utils/errorHandling";
import { InviteDTO } from "@reflag/shared/inviteAPI";

import { useInviteData } from "../data/useInviteData";
import { useOrgsData } from "../data/useOrgsData";

type AcceptInviteProps = {
  invite: InviteDTO;
  accept: () => Promise<boolean>;
};

function AcceptInvite({ invite, accept }: AcceptInviteProps) {
  const [loading, setLoading] = useState(false);
  return (
    <VStack spacing={6}>
      <Text textAlign="center">
        You&apos;ve been invited to join {invite.org.name} in Reflag!
      </Text>
      <HStack spacing={4}>
        <Button
          isLoading={loading}
          leftIcon={<RiCheckFill size={16} />}
          variant="solid"
          onClick={() => {
            setLoading(true);
            accept().then(setLoading);
          }}
        >
          Join {invite.org.name}
        </Button>
      </HStack>
    </VStack>
  );
}

export default function Invite() {
  const { signOut } = useAuth();
  const { user, org, setActiveOrg } = useEnvironmentUnsafe() ?? {};

  const { orgId } = useSlugParam("org");
  const { inviteKey } = useParams();
  const navigate = useNavigate();

  const toast = useToast();
  const errorToast = useErrorToast();

  const {
    data: inviteData,
    isError: inviteError,
    isLoading: inviteLoading,
  } = useInviteData(orgId, inviteKey);
  const {
    data: orgsData,
    isError: orgsError,
    isLoading: orgsLoading,
  } = useOrgsData();

  const isError = inviteError || orgsError;
  const isLoading = inviteLoading || orgsLoading;

  useEffect(() => {
    if (isError) {
      errorToast({
        title: `Failed to accept invite`,
        description: "Invitation code is invalid or expired",
        duration: 5000,
      });

      if (org) {
        navigate("/");
      } else {
        void signOut();
      }
    }
  }, [isError, errorToast, navigate, signOut, org]);

  const invite = inviteData?.data;
  const loaded = !isLoading && !isError && orgId && invite && orgsData;
  const isAlreadyMember =
    loaded && orgsData.data.find(({ id }) => id === orgId)?.userIsMember;

  if (isAlreadyMember) {
    return (
      <>
        <Helmet>
          <title>Already a Member</title>
        </Helmet>
        <VStack spacing={6}>
          <Heading fontSize="2xl">
            You&apos;re already part of {invite.org.name}!
          </Heading>
          <Button
            variant="outline"
            onClick={() =>
              org?.id !== orgId && setActiveOrg
                ? setActiveOrg(orgId, { refresh: true })
                : navigate("/")
            }
          >
            Continue
          </Button>
        </VStack>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Accept Organization Invite</title>
      </Helmet>
      {loaded && user ? (
        <VStack spacing={6}>
          <Heading fontSize="2xl">Hi {user.name},</Heading>
          <AcceptInvite
            accept={async () => {
              try {
                await api.post<"/invite/:inviteKey/accept">(
                  `/invite/${inviteKey}/accept`,
                );

                toast({
                  title: `You're now part of ${invite.org.name}!`,
                  status: "success",
                  onCloseComplete: () =>
                    setActiveOrg
                      ? setActiveOrg(orgId, { refresh: true })
                      : navigate("/"),
                });

                return true;
              } catch (error) {
                const { title, description } = translateError(
                  error,
                  `Failed to accept invite from ${invite.org.name}`,
                );

                errorToast({
                  title,
                  description,
                });

                return false;
              }
            }}
            invite={invite}
          />
        </VStack>
      ) : (
        <Spinner />
      )}
    </>
  );
}
